import { Box } from "@mui/material";
import { PropsWithChildren, useLayoutEffect, useRef, useState } from "react";

interface PlaceholderWrapperProps {
  /**
   * Whether to preserve the height of the children.
   * @default true to make the height of placeholder same as actual children
   */
  shouldPreserveHeight?: boolean;

  /** Whether to preserve the width of the children. Default to true to make the width of placeholder same as actual children */
  shouldPreserveWidth?: boolean;

  /** Whether the children are visible. Default to true */
  isVisible: boolean;
}

/**
 * A wrapper that preserves the size of its children when they are not visible.
 * Can be used to prevent layout shifts when conditionally rendering components.
 * Even though an alternative to set visibility: hidden is possible, this component
 * is a better approach to avoid screen readers reading hidden content or
 * manually change the visibility of the content in dev tools.
 */
export function PlaceholderWrapper({
  shouldPreserveHeight = true,
  shouldPreserveWidth = true,
  isVisible,
  children,
}: PropsWithChildren<PlaceholderWrapperProps>): JSX.Element {
  const ref = useRef<HTMLDivElement>(null);
  const [size, setSize] = useState<{ width: number; height: number } | null>(
    null
  );

  // Measure the child when it is visible. useLayoutEffect is used instead of useEffect,
  // as it fires synchronously after all DOM mutations.
  useLayoutEffect(() => {
    if (!ref.current) {
      return;
    }

    function updateSize(): void {
      if (ref.current) {
        const rect = ref.current.getBoundingClientRect();
        setSize({ width: rect.width, height: rect.height });
      }
    }
    updateSize();

    const observer = new ResizeObserver(updateSize);
    observer.observe(ref.current);

    return () => observer.disconnect();
  }, [isVisible]);

  if (isVisible) {
    // Render child in a measuring wrapper
    return (
      <Box ref={ref} display="inline-block">
        {children}
      </Box>
    );
  }

  // Render placeholder with last known size
  return (
    <Box
      sx={{
        width: shouldPreserveWidth ? size?.width ?? "auto" : "0px",
        height: shouldPreserveHeight ? size?.height ?? "auto" : "0px",
        visibility: "hidden",
        display: "inline-block",
      }}
    >
      {children}
    </Box>
  );
}
